import { cva, type VariantProps } from 'class-variance-authority'
import React, { ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from './cn'

export const iconButtonVariants = cva(
  [
    'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full border border-solid border-transparent p-0',
    'transition-[background,border-color,transform,opacity] duration-150 active:scale-95 motion-reduce:active:scale-100',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:cursor-not-allowed disabled:opacity-40',
  ],
  {
    variants: {
      variant: {
        ghost: 'bg-transparent text-fg enabled:hover:bg-[var(--plug-ghost-hover)]',
        secondary:
          'border-[var(--plug-secondary-border)] bg-[var(--plug-secondary)] text-fg enabled:hover:border-[var(--plug-secondary-border-hover)]',
        primary:
          'bg-[image:var(--plug-button)] text-on-accent enabled:hover:[filter:var(--plug-button-hover)]',
        danger: 'bg-danger-bg text-danger enabled:hover:brightness-125',
      },
      size: {
        sm: 'size-8 text-[16px]',
        md: 'size-10 text-[20px]',
        lg: 'size-12 text-[22px]',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  }
)

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  /** Required: icon-only buttons need an accessible name. */
  'aria-label': string
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant, size, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...props}
    />
  )
)
IconButton.displayName = 'IconButton'
